import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import * as S from './styles';
import Card, { Props } from './card';

const OBSERVER_OPTIONS = { threshold: 0, rootMargin: '450px' } as const;

const CardContent = (props: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [elementHeight, setElementHeight] = useState<number | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasCheckedInitialRef = useRef(false);

  const checkVisibility = useCallback(() => {
    if (!containerRef.current) return false;
    const rect = containerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < windowHeight + 150 && rect.bottom > -150;
  }, []);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry?.isIntersecting != null) setIsVisible(entry.isIntersecting);
  }, []);

  useEffect(() => {
    if (!isVisible || elementHeight != null) return;
    const el = contentRef.current;
    if (!el) return;
    const height = el.offsetHeight;
    if (height > 0) setElementHeight(height);
  }, [isVisible, elementHeight]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!hasCheckedInitialRef.current) {
      hasCheckedInitialRef.current = true;
      if (checkVisibility()) setIsVisible(true);
    }

    const prev = observerRef.current;
    if (prev) prev.disconnect();

    const observer = new IntersectionObserver(handleIntersection, OBSERVER_OPTIONS);
    observerRef.current = observer;
    observer.observe(container);

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [handleIntersection, checkVisibility]);

  const placeholderStyle = useMemo(
    () => ({ height: (elementHeight ?? 'auto') as number | 'auto' }),
    [elementHeight]
  );

  return (
    <S.CardContainer ref={containerRef} id={`client-${props.client.COD_CLIENTE}`}>
      {isVisible ? (
        <S.ContentWrapper ref={contentRef}>
          <Card {...props} />
        </S.ContentWrapper>
      ) : (
        <S.Placeholder style={placeholderStyle} />
      )}
    </S.CardContainer>
  );
};

export default React.memo(CardContent);
